// Original wallpapers made of soft colour blobs. One description drives both CSS (preview) and canvas (export).

export interface WallBlob {
  x: number; // 0–1 of width
  y: number; // 0–1 of height
  r: number; // radius as fraction of height
  color: string;
}

export interface Wallpaper {
  id: string;
  name: string;
  from: string;
  to: string;
  blobs: WallBlob[];
  dark: boolean;
}

export const WALLPAPERS: Wallpaper[] = [
  {
    id: 'tide',
    name: 'Tide',
    from: '#1E4FD8',
    to: '#0B2A7A',
    blobs: [
      { x: 0.15, y: 0.25, r: 0.42, color: '#2FB5A3' },
      { x: 0.95, y: 0.08, r: 0.32, color: '#4A7CFF' },
      { x: 0.55, y: 0.95, r: 0.35, color: '#0E3BA8' },
    ],
    dark: true,
  },
  {
    id: 'meadow',
    name: 'Meadow',
    from: '#C9E9A8',
    to: '#4FA36B',
    blobs: [
      { x: 0.2, y: 0.2, r: 0.35, color: '#F6F2A0' },
      { x: 0.9, y: 0.55, r: 0.4, color: '#2E7D5B' },
    ],
    dark: false,
  },
  {
    id: 'sunset',
    name: 'Sunset',
    from: '#FFB36B',
    to: '#B23A6B',
    blobs: [
      { x: 0.8, y: 0.2, r: 0.35, color: '#FFE08A' },
      { x: 0.1, y: 0.8, r: 0.45, color: '#6B2A8C' },
    ],
    dark: true,
  },
  {
    id: 'blush',
    name: 'Blush',
    from: '#FFE3EA',
    to: '#F4A6B8',
    blobs: [
      { x: 0.85, y: 0.15, r: 0.3, color: '#FFFFFF' },
      { x: 0.2, y: 0.85, r: 0.35, color: '#E88CA6' },
    ],
    dark: false,
  },
  {
    id: 'night',
    name: 'Night',
    from: '#1B1D2E',
    to: '#050608',
    blobs: [
      { x: 0.7, y: 0.25, r: 0.3, color: '#3A2F6B' },
      { x: 0.2, y: 0.75, r: 0.35, color: '#16324F' },
    ],
    dark: true,
  },
  {
    id: 'sand',
    name: 'Sand',
    from: '#F3E6CF',
    to: '#D9BE93',
    blobs: [
      { x: 0.2, y: 0.3, r: 0.35, color: '#FFF7E8' },
      { x: 0.85, y: 0.8, r: 0.3, color: '#C49A6C' },
    ],
    dark: false,
  },
  {
    id: 'lemon',
    name: 'Lemon',
    from: '#FFF27A',
    to: '#F1C400',
    blobs: [
      { x: 0.8, y: 0.25, r: 0.32, color: '#FFFFFF' },
      { x: 0.15, y: 0.85, r: 0.35, color: '#E8A800' },
    ],
    dark: false,
  },
  {
    id: 'mono',
    name: 'Graphite',
    from: '#5B5E66',
    to: '#202226',
    blobs: [{ x: 0.5, y: 0.2, r: 0.4, color: '#8A8E96' }],
    dark: true,
  },
];

export function getWallpaper(id: string): Wallpaper {
  return WALLPAPERS.find((w) => w.id === id) ?? WALLPAPERS[0];
}

/** CSS equivalent of paintWallpaper for a portrait (~1:2.17) box. Blob radii are fractions of the height. */
export function wallpaperCss(w: Wallpaper): string {
  const blobs = w.blobs.map((b) => `radial-gradient(${b.r * 217}% ${b.r * 100}% at ${b.x * 100}% ${b.y * 100}%, ${b.color} 0%, ${b.color}00 100%)`);
  return [...blobs, `linear-gradient(160deg, ${w.from}, ${w.to})`].join(', ');
}

export function paintWallpaper(ctx: CanvasRenderingContext2D, w: Wallpaper, width: number, height: number): void {
  const g = ctx.createLinearGradient(0, 0, width * 0.35, height);
  g.addColorStop(0, w.from);
  g.addColorStop(1, w.to);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, width, height);
  for (const b of w.blobs) {
    const cx = b.x * width;
    const cy = b.y * height;
    const r = b.r * height;
    const rg = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    rg.addColorStop(0, b.color);
    rg.addColorStop(1, `${b.color}00`);
    ctx.fillStyle = rg;
    ctx.fillRect(0, 0, width, height);
  }
}

export interface Device {
  id: string;
  name: string;
  /** CSS-pixel screen size. */
  w: number;
  h: number;
  /** Export scale (device pixel ratio). */
  dpr: number;
  radius: number;
}

export const DEVICES: Device[] = [
  { id: 'iphone-pro-max', name: 'iPhone Pro Max (6.9″)', w: 440, h: 956, dpr: 3, radius: 62 },
  { id: 'iphone', name: 'iPhone (6.1″)', w: 393, h: 852, dpr: 3, radius: 55 },
  { id: 'iphone-se', name: 'iPhone SE', w: 375, h: 667, dpr: 2, radius: 0 },
  { id: 'pixel', name: 'Android 20:9', w: 412, h: 915, dpr: 2.625, radius: 40 },
  { id: 'android-compact', name: 'Android 19.5:9', w: 393, h: 851, dpr: 2.75, radius: 36 },
];

export function getDevice(id: string): Device {
  return DEVICES.find((d) => d.id === id) ?? DEVICES[0];
}
